import './zmenaStadia.scss';
import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button, Checkbox, CheckboxGroup, Col, Details, FormGroup, Label, LoaderButton, Row, Select,
} from 'idsk-react-components';
import { BackButton, ConfirmModal } from 'slovlex-idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import LegislativnyMaterialDetail from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import ZmenaStadiaRequest from '../../../model/admin/ZmenaStadiaRequest';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import routes from '../../../routing/routes';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import { useClient } from '../../../context/ClientStatusContext';
import { changeMaterialStadium } from '../../../client/admin/zmenaStadiaClient';
import useSablonaStadium from './hook/useSablonaStadium';

interface Props {
  material: LegislativnyMaterialDetail;
}

const ZmenaStadia = ({ material }: Props): ReactElement => {
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number }>();
  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();
  const { displaySuccess } = useSuccessMessageContext();
  const navigate = useNavigate();
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    wrapClient: zmenaStadiaClient,
    status: zmenaStadiaStatus,
  } = useClient(changeMaterialStadium);

  const {
    stadiumList,
  } = useSablonaStadium(material?.sablonaProcesu?.uuid);

  const [zmenaStadiaRequest, setZmenaStadiaRequest] = useState<ZmenaStadiaRequest>({
    stadium: undefined,
    typMaterialuKod: undefined,
    skipServiceTasks: false,
  });

  useEffect(() => {
    if (stadiumList && material) {
      updateField(
        'stadium',
        stadiumList[0]?.hodnota,
      );
      updateField('typMaterialuKod', material?.typ?.kod);
    }
  }, [material, stadiumList]);

  const updateField: SafeParameterSetter<ZmenaStadiaRequest> = (fieldName, value) => {
    setZmenaStadiaRequest(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const zmenaStadiaClick = async (): Promise<void> => {
    await zmenaStadiaClient(type, year, index, zmenaStadiaRequest);
    navigate(routes.procesy.detail.withParams(type, year, index));
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  return (
    <div className="zmena-stadia">
      <FormGroup error={!!getFieldErrorMessage('zmena-stadia')}>
        <Row>
          <Col widthDesktop="one-third">
            <Label htmlFor="zmena-stadia">Nové štádium materiálu</Label>
          </Col>
        </Row>
        <Row>
          <Col widthDesktop="two-thirds">
            <Select
              id="zmena-stadia"
              width="two-thirds"
              name="zmena-stadia"
              value={zmenaStadiaRequest.stadium}
              onChange={
                ({ target }) => {
                  updateField('stadium', target.value);
                  clearFieldErrorMessage('zmena-stadia');
                }
              }
              error={getFieldErrorMessage('zmena-stadia')}
            >
              {(stadiumList && stadiumList?.length > 0) ? (
                <>
                  {stadiumList
                    ?.sort((a, b) => Intl.Collator()
                      .compare(a.nazov, b.nazov))?.map((stadium) => (
                        <option key={stadium.hodnota} value={stadium.hodnota}>
                          {stadium.nazov}
                        </option>
                    ))}
                </>
              ) : (
                <option key="emptyOption" value="emptyOption" disabled>
                  Nenašiel sa žiadny záznam.
                </option>
              )}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col widthDesktop="two-thirds">
            <CheckboxGroup small className="zmena-stadia__checkbox-wrapper">
              <Checkbox
                className="zmena-stadia__checkbox"
                id="historia-change-checkbox"
                onChange={({ target }) => updateField('skipServiceTasks', target.checked)}
                checked={zmenaStadiaRequest.skipServiceTasks}
              >
                Neodosielať notifikácie, neindexovať a nerobiť zmeny v histórii pri tejto zmene štádia
              </Checkbox>
            </CheckboxGroup>
          </Col>
        </Row>
      </FormGroup>
      <Row>
        <Col widthDesktop="two-thirds">
          <Details
            className="zmena-stadia-explanation"
            summary="Detailnejšie vysvetlenie funkcionality"
          >
            Pri bežnej zmene štádia nechať pole nezaškrtnuté. Zaškrtnutie sa používa napr.
            keď je potrebné vykonať zmenu metadát na ukončenom procese bez toho,
            aby boli notifikovaní používatelia a zmenené štádium procesu.
            Bližšie informácie sa nachádzajú v administrátorskej príručke.
          </Details>
        </Col>
      </Row>
      <div className="zmena-stadia__buttons-container">
        <ConfirmModal
          question="Naozaj si prajete zmeniť aktuálne štádium materiálu?"
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              size="large"
              className="zmena-stadia__submit-button"
              type="button"
              loading={zmenaStadiaStatus.loading}
              disableOutsideClickWhileLoading
            >Zmeniť štádium materiálu
            </LoaderButton>
          )}
          doAfterConfirm={() => zmenaStadiaClick()}
        />
        <BackButton />
      </div>
    </div>
  );
};

export default ZmenaStadia;
